'use client';

import { useTranslations } from 'next-intl';
import * as React from 'react';
import { toast } from 'sonner';

import { useAddEventToFavorites, useRemoveEventFromFavorites } from '@/lib/api/event/hooks';
import { HotFuturesCard } from '@/lib/components/HomeSidebar/HotFuturesCard';
import { LeaderboardCard } from '@/lib/components/HomeSidebar/LeaderboardCard';
import { useAuthSession } from '@/lib/hooks/useAuth';
import { useRouter } from '@/lib/routing';
import { EventBanner, type EventBannerEvent } from '@/lib/ui-kit-v2/event-banner';

interface HomeTopSectionProps {
  events: EventBannerEvent[];
  /** The request carried a session cookie — the side cards' layout until auth answers. */
  hasSession: boolean;
}

export function HomeTopSection({ events, hasSession }: HomeTopSectionProps) {
  const t = useTranslations('event');
  const { user } = useAuthSession();
  const router = useRouter();
  const addToFavorites = useAddEventToFavorites();
  const removeFromFavorites = useRemoveEventFromFavorites();

  const [favorites, setFavorites] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(events.map((ev) => [ev.id ?? '', Boolean(ev.isFavorite)])),
  );

  const bannerEvents = React.useMemo<EventBannerEvent[]>(
    () =>
      events.map((ev) => ({
        ...ev,
        isFavorite: ev.id ? (favorites[ev.id] ?? ev.isFavorite) : ev.isFavorite,
      })),
    [events, favorites],
  );

  const handleBookmark = React.useCallback(
    (event: EventBannerEvent) => {
      if (!event.id) return;

      if (!user) {
        toast.error(t('loginToFavorite'));
        return;
      }

      const eventId = event.id;
      const wasFavorite = favorites[eventId] ?? Boolean(event.isFavorite);

      setFavorites((prev) => ({ ...prev, [eventId]: !wasFavorite }));

      const revert = () => {
        setFavorites((prev) => ({ ...prev, [eventId]: wasFavorite }));
      };

      if (wasFavorite) {
        removeFromFavorites.mutate(eventId, {
          onError: () => {
            revert();
            toast.error(t('favoriteRemoveError'));
          },
        });
      } else {
        addToFavorites.mutate(eventId, {
          onError: () => {
            revert();
            toast.error(t('favoriteAddError'));
          },
        });
      }
    },
    [user, favorites, addToFavorites, removeFromFavorites, t],
  );

  const handleEventClick = React.useCallback(
    (event: EventBannerEvent) => {
      if (!event.slug) return;
      router.push(`/event/${event.slug}`);
    },
    [router],
  );

  if (events.length === 0) return null;

  return (
    <section className="grid gap-4 min-[1024px]:grid-cols-[minmax(0,1fr)_350px]">
      <EventBanner
        events={bannerEvents}
        onBookmark={handleBookmark}
        onEventClick={handleEventClick}
      />
      <div className="hidden flex-col gap-4 min-[1024px]:flex">
        <HotFuturesCard hasSession={hasSession} />
        <LeaderboardCard hasSession={hasSession} />
      </div>
    </section>
  );
}
